'use client'
import type { FC } from 'react'
import { useMemo } from 'react'
import { Box, Button, HStack, Icon, MenuList, Skeleton } from '@chakra-ui/react'
import { AiFillFilter } from 'react-icons/ai'
import { overrideText } from 'ui/lib/overrides'
import { Currency } from 'ecosystem'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import FilterRangeSelector from '../ProductsFilter/FilterRangeSelector'
import FilterOptions from '../ProductsFilter/FilterOptions'
import { filterButtonProps, FilterMenus } from '../../ProductsFilterComponents'
import {
  type ProductListSearchMoreFiltersOverrides,
  type ProductListSearchMoreFiltersProps
} from '../types'
import { FILTERS_BEFORE_SHOW_MORE } from '../../../constants'

interface ProductListSearchHorizontalFiltersOverrides extends ProductListSearchMoreFiltersOverrides {
  showMoreFilters?: string
}

export type ProductListSearchHorizontalFiltersProps = ProductListSearchMoreFiltersProps & {
  onMore?: () => void
}

export const ProductListSearchHorizontalFilters: FC<ProductListSearchHorizontalFiltersProps> = ({
  searchProductsState,
  updateSearchDelayed,
  clearFilters,
  updateCategoryFilterItemSelected,
  updateBrandFilterItemSelected,
  updateSelectedPriceRange,
  updateOptionFilterItemSelected,
  onMore,
  filtersBeforeShowMore = FILTERS_BEFORE_SHOW_MORE,
  isLoading: _isLoading,
  onSubmit: _onSubmit,
  showSubmit: _showSubmit,
  ...props
}) => {
  const overrides = useOverridesContext<keyof ProductListSearchHorizontalFiltersOverrides>()
  const { availableFilters, selectedFilters, initStatus } = searchProductsState
  const isInit = initStatus === 'SUCCESS'

  const availablePriceRange = useMemo(
    () =>
      Number.isInteger(availableFilters.minPrice) && Number.isInteger(availableFilters.maxPrice)
        ? ([availableFilters.minPrice, availableFilters.maxPrice] as [number, number])
        : undefined,
    [availableFilters.maxPrice, availableFilters.minPrice]
  )

  const selectedPriceRange = useMemo(
    () =>
      Number.isInteger(selectedFilters.minPrice) && Number.isInteger(selectedFilters.maxPrice)
        ? ([selectedFilters.minPrice, selectedFilters.maxPrice] as [number, number])
        : undefined,
    [selectedFilters.maxPrice, selectedFilters.minPrice]
  )

  const showFiltersCount = useMemo(() => {
    // Categories, brands, and price filters are always shown if available
    const defaultFiltersCount =
      (availableFilters.categories?.length ? 1 : 0) +
      (availableFilters.brands?.length ? 1 : 0) +
      (availablePriceRange ? 1 : 0)
    return Math.max(0, filtersBeforeShowMore - defaultFiltersCount)
  }, [
    availableFilters.categories?.length,
    availableFilters.brands?.length,
    availablePriceRange,
    filtersBeforeShowMore
  ])

  const showMoreButton = useMemo(() => {
    return availableFilters.options.length > showFiltersCount
  }, [availableFilters.options.length, showFiltersCount])

  const counterPriceFilters = useMemo(() => {
    if (!selectedPriceRange || !availablePriceRange) return 0

    let counter = selectedPriceRange[0] !== availablePriceRange[0] ? 1 : 0

    if (selectedPriceRange[1] !== availablePriceRange[1]) {
      counter += 1
    }

    return counter
  }, [selectedPriceRange, availablePriceRange])

  if (!isInit) {
    return (
      <HStack alignItems="flex-start" gap={2} w="auto" wrap="wrap" {...props}>
        <Skeleton h="35px" w="120px" />
        <Skeleton h="35px" w="120px" />
        <Skeleton h="35px" w="120px" />
        <Skeleton h="35px" w="100px" />
      </HStack>
    )
  }

  return (
    <HStack alignItems="flex-start" gap={2} w="auto" wrap="wrap" {...props}>
      {/* Categories Filter */}
      {availableFilters.categories?.length && (
        <FilterMenus
          filterName={overrideText('Kategorier', overrides?.filterCategoriesTitle)}
          counter={selectedFilters.categories.length}
          list={
            <MenuList zIndex={3} maxH="60vh" overflowY="auto">
              <FilterOptions
                onChange={({ id, isSelected }) => {
                  updateCategoryFilterItemSelected({ id, isSelected })
                  updateSearchDelayed()
                }}
                options={availableFilters.categories}
                selectedOptions={selectedFilters.categories}
              />
            </MenuList>
          }
        />
      )}

      {/* Brands Filter */}
      {availableFilters.brands?.length && (
        <FilterMenus
          filterName={overrideText('Varumärken', overrides?.filterBrandsTitle)}
          counter={selectedFilters.brands.length}
          list={
            <MenuList zIndex={3} maxH="60vh" overflowY="auto">
              <FilterOptions
                onChange={({ id, isSelected }) => {
                  updateBrandFilterItemSelected({ id, isSelected })
                  updateSearchDelayed()
                }}
                options={availableFilters.brands}
                selectedOptions={selectedFilters.brands}
              />
            </MenuList>
          }
        />
      )}

      {/* Option Filters - Limited by showFiltersCount */}
      {availableFilters.options.map(
        (group, index) =>
          index < showFiltersCount && (
            <FilterMenus
              key={group.name}
              filterName={group.name}
              counter={selectedFilters.options[group.name]?.length || 0}
              list={
                <MenuList zIndex={3} maxH="60vh" overflowY="auto">
                  <Box>
                    <FilterOptions
                      onChange={({ id, isSelected }) => {
                        updateOptionFilterItemSelected({
                          id,
                          isSelected,
                          groupName: group.name
                        })
                        updateSearchDelayed()
                      }}
                      options={group.values.map(({ id, value: name, count }) => ({
                        id,
                        name,
                        count
                      }))}
                      selectedOptions={selectedFilters.options[group.name] || []}
                    />
                  </Box>
                </MenuList>
              }
            />
          )
      )}

      {/* Price Range Filter */}
      {availablePriceRange && availablePriceRange[0] !== availablePriceRange[1] && (
        <FilterMenus
          filterName={overrideText('Pris', overrides?.filterPriceRangeTitle)}
          counter={counterPriceFilters}
          list={
            <MenuList zIndex={3}>
              <FilterRangeSelector
                px={3}
                rangeProps={{
                  minWidth: { base: 200, md: 300 },
                  value: selectedPriceRange,
                  defaultValue: availablePriceRange,
                  min: availablePriceRange[0],
                  max: availablePriceRange[1],
                  onChange: (values) => {
                    updateSelectedPriceRange(values as [number, number])
                    updateSearchDelayed()
                  }
                }}
                unitsLabel={overrideText(Currency.SEK, overrides?.currency)}
              />
            </MenuList>
          }
        />
      )}

      {/* Show More Filters Button */}
      {showMoreButton && onMore && (
        <Button
          {...filterButtonProps}
          onClick={onMore}
          leftIcon={<Icon as={AiFillFilter} />}
          minH="33px"
          h="auto"
        >
          {overrideText('Visa fler filter', overrides?.showMoreFilters)}
        </Button>
      )}

      {/* Clear Filters Button */}
      <Button size="sm" onClick={clearFilters} minH="33px" h="auto">
        {overrideText('Återställ', overrides?.filterClear)}
      </Button>
    </HStack>
  )
}
